import { makeStyles } from '@material-ui/core';
import axios from 'axios';
import clsx from 'clsx';
import { add } from 'date-fns';
import React from 'react';
import { ArrowRight, CheckCircle } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { enumToIcon, enumToStatusType } from '../../../types/common';
import { ProjectSummary } from '../../../types/project';
import { Calendar, CalendarEvent, CalendarView } from '../../global/calendar/Calendar';
import { Badge, StatusIcon } from '../../global/Status';

const useProjectEventClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        color: theme.colors.neutral[1],
        '& > span': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            ...theme.fonts.caption,
            color: theme.colors.neutral[2],
        },
        '&.tooltip > span': { whiteSpace: 'normal' },
        '& > span:first-child': {
            color: theme.colors.neutral[1],
            ...theme.fonts.labelSemiBold,
        },
        '& > hr': {
            margin: '8px 0',
            borderColor: theme.colors.neutral[6],
        },
    },
    badges: {
        display: 'flex',
        gap: 5,
        height: 27,
        '& > span:last-child': {
            alignSelf: 'center',
            color: theme.colors.neutral[2],
            ...theme.fonts.caption,
        },
    },
    date: {
        display: 'flex',
        alignItems: 'center',
    },
});

type ProjectData = {
    project: ProjectSummary;
    forecast?: 'internal' | 'external';
};

const colors = Object.freeze({
    'past due': theme.colors.red[1],
    forecast: theme.colors.neutral[3],
    'ready to inspect': theme.colors.blue[2],
    'in progress': theme.colors.green[4],
    'awaiting report': theme.colors.amber[2],
    'under review': theme.colors.amber[2],
    completed: theme.colors.neutral[2],
} as { [key: string]: string });

/**
 * Render the contents of a calendar event.
 */
const ProjectEvent = (props: { event: CalendarEvent; view: CalendarView; tooltip: boolean }) => {
    const classes = useProjectEventClasses();
    const { t, Trans } = usePrefixTranslation(['project', 'common'], {
        project: 'common.ProjectCalendar.ProjectEvent',
    });
    const { project, forecast } = props.event.data as ProjectData;
    const type = enumToStatusType[!!forecast ? 'forecast' : project.status!];
    const icon = enumToIcon[!!forecast ? 'forecast' : project.status!];
    const cls = { tooltip: props.tooltip };

    const date = cls.tooltip && (
        <Trans
            i18nKey={!!forecast ? 'date' : 'range'}
            values={{
                date: forecast === 'internal' ? project.internal_due_date : project.external_due_date,
                start: project.status !== 'completed' ? project.estimated_start_date : project.in_progress_date,
                end: project.status !== 'completed' ? project.estimated_completed_date : project.completed_date,
            }}
            components={{ arrow: <ArrowRight style={{ width: 15, height: 15 }} /> }}
        />
    );

    return (
        <div className={clsx(classes.root, cls)}>
            <span>{props.event.name}</span>
            <span>{project.code}</span>
            <div className={classes.badges}>
                {!forecast && <StatusIcon type={type} icon={icon} />}
                {project.psm && <StatusIcon type='neutral' icon={<CheckCircle />} />}
                {(forecast === 'internal' || (project.methods && 'internal' in project.methods)) && (
                    <Badge type='neutral'>{t('common:type.short-internal')}</Badge>
                )}
                {(forecast === 'external' || (project.methods && 'external' in project.methods)) && (
                    <Badge type='neutral'>{t('common:type.short-external')}</Badge>
                )}
                {project.rope_access && <StatusIcon type='neutral' icon={'R'} />}
                {project.internal_scaffold && <StatusIcon type='neutral' icon={'S'} />}
                {project.heavy_cleaning && <StatusIcon type='neutral' icon={'C'} />}
            </div>
            {cls.tooltip && <hr />}
            {cls.tooltip && <span className={classes.date}>{date}</span>}
        </div>
    );
};

/**
 * Group order used by some calendar views.
 */
const groupOrder: { [group: string]: number } = {
    'past due': 0,
    forecast: 1,
    'ready to inspect': 2,
    'in progress': 3,
    'awaiting report': 4,
    'under review': 5,
    completed: 6,
};

/**
 * Project calendar component.
 * This component extends on the base calendar component and provides render logic for the calendar events, data
 * fetching, and drag and drop.
 *
 * @param editable if the calendar events can be edited through drag and drop
 * @param initialView initial calendar view, defaults to `'month'`
 * @param initialAnchor initial calendar anchor, defaults to current day
 * @param setViewRef mutable reference to get access to the view setter
 * @param setAnchorRef mutable reference to get access to the anchor setter
 * @param onViewChange callback triggered the view changes
 * @param onAnchorChange callback triggered the anchor change
 * @param onIntervalChange callback triggered when the time interval changes
 * @param onFilter callback triggered when the events displayed in the view change
 * @param onSelected callback triggered when an event is clicked
 * @param header content to be rendered in the header
 */
export const ProjectCalendar = ({
    editable,
    initialView,
    initialAnchor,
    setViewRef,
    setAnchorRef,
    onViewChange,
    onAnchorChange,
    onIntervalChange,
    onFilter,
    onSelected,
    header,
}: {
    editable?: boolean;
    initialView?: CalendarView;
    initialAnchor?: Date;
    setViewRef?: React.MutableRefObject<(view: CalendarView) => void>;
    setAnchorRef?: React.MutableRefObject<(anchor: Date) => void>;
    onViewChange?: (view: CalendarView, anchor: Date, interval: Interval) => void;
    onAnchorChange?: (view: CalendarView, anchor: Date, interval: Interval) => void;
    onIntervalChange?: (view: CalendarView, anchor: Date, interval: Interval) => void;
    onFilter?: (events: CalendarEvent<ProjectData>[]) => void;
    onSelected?: (event: CalendarEvent<ProjectData>) => void;
    groupSort?: (a: string, b: string) => number;
    header?: React.ReactNode;
}) => {
    const [interval, setInterval] = React.useState<Interval>({ start: new Date(), end: new Date() });
    const [projects, setProjects] = React.useState<ProjectSummary[]>([]);
    const [draggedEvents, setDraggedEvents] = React.useState<{ [key: number]: CalendarEvent }>({});

    const memoizedEvents = React.useMemo(() => {
        const events: CalendarEvent[] = [];
        projects.forEach(p => {
            if (p.inspection != undefined && p.inspection !== -1)
                events.push({
                    name: p.name,
                    group: p.status!,
                    color: colors[p.status!],
                    draggable: p.status === 'ready to inspect',
                    data: { project: p } as ProjectData,
                    key: events.length,
                    interval: { start: p.estimated_start_date!, end: p.estimated_completed_date! },
                    render: props => <ProjectEvent {...props} />,
                });
            else if (p.plan != undefined) {
                if (p.external_activity_required)
                    events.push({
                        name: p.name,
                        group: 'forecast',
                        color: colors['forecast'],
                        interval: { start: p.external_due_date!, end: p.external_due_date! },
                        draggable: true,
                        key: events.length,
                        data: { project: p, forecast: 'external' } as ProjectData,
                        render: props => <ProjectEvent {...props} />,
                    });
                if (p.internal_activity_required)
                    events.push({
                        name: p.name,
                        group: 'forecast',
                        color: colors['forecast'],
                        interval: { start: p.internal_due_date!, end: p.internal_due_date! },
                        draggable: true,
                        key: events.length,
                        data: { project: p, forecast: 'internal' } as ProjectData,
                        render: props => <ProjectEvent {...props} />,
                    });
            }
        });
        return events;
    }, [projects]);

    const replacedEvents = React.useMemo(() => {
        const events = [...memoizedEvents];
        Object.values(draggedEvents).forEach(event => (events[event.key as number] = event));
        return events;
    }, [memoizedEvents, draggedEvents]);

    const fetchProjects = async (interval: Interval) => {
        const params = { pagination: 'False', start_date: interval.start, end_date: interval.end };
        const response = await axios.get<ProjectSummary[]>('/inspections/overview/', { params });
        setInterval(interval);
        setProjects(response.data);
    };

    const onDnD = async (event: CalendarEvent, duration: Duration) => {
        const { project: projectSummary, forecast } = event.data as ProjectData;
        const draggedEvent = {
            ...event,
            interval: { start: add(event.interval.start, duration), end: add(event.interval.end, duration) },
        };
        setDraggedEvents({ ...draggedEvents, [draggedEvent.key as number]: draggedEvent });
        try {
            if (!forecast) {
                const project = (await axios.get(`/inspections/${projectSummary.inspection}/`)).data;
                await axios.put(`/inspections/${projectSummary.inspection}/`, {
                    ...project,
                    estimated_start_date: draggedEvent.interval.start,
                    estimated_completed_date: draggedEvent.interval.end,
                });
            } else {
                const plan = (await axios.get(`/inspection-plans/${projectSummary.plan}/`)).data;
                await axios.put(
                    `/inspection-plans/${projectSummary.plan}/`,
                    forecast === 'internal'
                        ? { ...plan, internal_due_date: draggedEvent.interval.start }
                        : { ...plan, external_due_date: draggedEvent.interval.start },
                );
            }
            await fetchProjects(interval);
        } finally {
            const nextDraggedEvents = { ...draggedEvents };
            delete nextDraggedEvents[draggedEvent.key as number];
            setDraggedEvents(nextDraggedEvents);
        }
    };

    return (
        <Calendar
            events={replacedEvents}
            initialView={initialView}
            initialAnchor={initialAnchor}
            setViewRef={setViewRef}
            setAnchorRef={setAnchorRef}
            onViewChange={onViewChange}
            onAnchorChange={onAnchorChange}
            onIntervalChange={(view, anchor, interval) => (
                fetchProjects(interval), onIntervalChange?.(view, anchor, interval)
            )}
            onFilter={onFilter}
            onSelected={onSelected}
            onDnD={editable ? onDnD : undefined}
            groupSort={(a, b) => groupOrder[a] - groupOrder[b]}
            header={header}
        />
    );
};
